import {useIntl} from 'react-intl';
import * as React from 'react';
import {useEffect, useState, useRef} from 'react';
import {HeaderButton, ReactiveTable} from "react-ive-tables";
import {ComponentContainer} from "../../../lib/template/ComponentContainer";
import {LazyFetchingService} from "../../../lib/services/lazy-fetching-service";
import {
    AdvancePaymentResponse,
    AdvancePaymentSubtotal,
    InvoiceWithPayment, UnprocessedBankTransactionResponse,
    UnrecognizedBankTransaction
} from "../../../lib/types/types";
import useSWR, {mutate} from "swr";
import {formatNumber} from "../../../lib/common";
import {Button} from "primereact/button";
import {Badge} from "primereact/badge";
import {IDropdownOption} from "@xal3xfx/react-utils/dist/util-service";
import {Dialog} from "primereact/dialog";
import {GroupCardEditForm} from "../../card-system/cards/groupCardEditForm/GroupCardEditForm";
import AddIBAN from "./addIban/AddIBAN";
import moment from "moment";
import {BankTransactionService} from "../../../lib/services/bank-transaction-service";
import {useToast} from "../../../lib/hooks/useToast";

interface Props {

}

const tableMutationKey = "/bank/transactions/unrecognized/lazy";

const lazyFetchingService = new LazyFetchingService<UnrecognizedBankTransaction>(tableMutationKey);

const UnrecognizedTransactions : React.FC<Props> = props => {
    const {formatMessage: f} = useIntl();
    const didMountRef = useRef(false);
    const toastHelpers = useToast();

    const [showAddIbanDialog, setShowAddIbanDialog] = useState(false);
    const [selectedUnrecognizedTransaction, setSelectedUnrecognizedTransaction] = useState<UnrecognizedBankTransaction>();

    const fetchData = lazyFetchingService.getDataFetcher;
    const fetcher = lazyFetchingService.getLazyFetcher;

    const bankService = new BankTransactionService(f, toastHelpers)

    const {data: unrecognizedPaymentsTableData} = useSWR<Awaited<ReturnType<typeof fetcher>>, Error>(tableMutationKey, {fetcher: fetcher});

    useEffect(() => {
        if(!didMountRef.current) {
            didMountRef.current = true;
        }
    }, []);

    const getColumnTemplate = () => {
        return {
            valueDate: (rowData: UnrecognizedBankTransaction) => moment(rowData.valueDate).format("DD-MM-YYYY"),
            amount: (rowData: UnrecognizedBankTransaction) => formatNumber(rowData.amount),
            recognitionError: (rowData: UnrecognizedBankTransaction) => f({id: rowData.recognitionError}),
            operations: (rowData: UnrecognizedBankTransaction) => <div className={"flex justify-content-center gap-1"}>
                <Button icon={'pi pi-plus-circle'} className={'mr-2'}
                        tooltip={f({id: 'addIBAN'})}
                        tooltipOptions={{position: 'top'}}
                        onClick={() => {
                            setSelectedUnrecognizedTransaction(rowData)
                            setShowAddIbanDialog(true);
                        }}/>
            </div>
        }
    }

    const onIbanAdded = () => {
        setShowAddIbanDialog(false);
        setSelectedUnrecognizedTransaction(undefined);
        mutate(tableMutationKey);
    }

    const initiateRecognize = () => {
        bankService.recognizeAll()
            .then(resp => {
                mutate(tableMutationKey);
            }).catch()
    }

    const rightHeaderButtons: HeaderButton[] = [
        {
            icon: 'pi pi-sync',
            onClick: initiateRecognize,
            className: '',
            tooltipLabel: f({id: 'recognize'})
        }
    ]

    return <>
        <ComponentContainer componentName={f({ id: "unrecognizedTransactions" })}>

        <ReactiveTable
            swr
            data={unrecognizedPaymentsTableData?.rows}
            showHeader={true}
            rightHeaderButtons={rightHeaderButtons}
            totalRecords={unrecognizedPaymentsTableData?.totalRecords}
            fetchData={fetchData}
            ignoreFilters={['operations', 'recognitionError']}
            columnTemplate={getColumnTemplate()}
            columnOrder={['bankReference', 'valueDate', 'amount', 'paymentDetails', 'correspondentAccountNumber', 'correspondentName', 'recognitionError' ,'operations']}
            columnStyle={{
                operations: {
                    header: {},
                    body: {width: '1px'}
                }
            }}
        />
        </ComponentContainer>

        {showAddIbanDialog && (
            <Dialog breakpoints={{'960px': '75vw', '640px': '100vw'}} style={{width: '50vw'}} header={f({id: 'addIBANForClient'}, {clientName: selectedUnrecognizedTransaction?.correspondentName})}
                    onHide={() => setShowAddIbanDialog(false)} visible={showAddIbanDialog}
            >
                <AddIBAN iban={selectedUnrecognizedTransaction?.correspondentAccountNumber} onCreatedCb={onIbanAdded} clientNameSuggestion={selectedUnrecognizedTransaction?.correspondentName}/>
            </Dialog>
        )}
    </>
};

export default UnrecognizedTransactions
